import React, { useEffect, useState, useCallback, useRef } from "react";
import { Col, Row, Spinner, Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FiArrowUpRight, FiCheckCircle, FiChevronLeft, FiChevronRight,
  FiClock, FiDollarSign, FiDownload, FiInfo, FiRefreshCw,
  FiShield, FiSmartphone, FiTrendingUp, FiXCircle, FiAlertTriangle,
  FiActivity, FiList, FiSend, FiEye, FiEyeOff,
} from "react-icons/fi";
import {
  apiVendorWalletBalance,
  apiVendorWalletTransactions,
  apiVendorWithdrawalRequest,
  apiVendorWithdrawalHistory,
  apiVendorWithdrawalStatus,
  apiVendorCalculateFees,
} from "../api/index.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import { vendorOwnerIdOf } from "../utilities/vendorScope";

// ── Power BI design tokens ────────────────────────────────────────────────────
const C = {
  bg:           "#f4f6fb",
  panel:        "#ffffff",
  header:       "#1a2b4a",
  accent:       "#e63757",
  accentSoft:   "#fff0f3",
  accentBorder: "#f9c8d0",
  blue:         "#2c7be5",
  blueSoft:     "#eaf2ff",
  teal:         "#00b69b",
  tealSoft:     "#e6faf7",
  orange:       "#f6961e",
  orangeSoft:   "#fff4e6",
  purple:       "#7048e8",
  purpleSoft:   "#f1edff",
  text:         "#1a2b4a",
  textMid:      "#4a5568",
  textLight:    "#8898aa",
  border:       "#e2e8f0",
  inputBg:      "#f8faff",
  shadow:       "0 2px 12px rgba(26,43,74,0.08)",
  shadowHover:  "0 4px 20px rgba(26,43,74,0.14)",
  radius:       "10px",
  radiusSm:     "6px",
};

const S = {
  page: { background: C.bg, minHeight: "100vh", padding: "0 0 48px 0" },
  hero: {
    background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
    padding: "28px 32px 24px", marginBottom: 28, borderRadius: C.radius,
    color: "#fff", display: "flex", alignItems: "flex-start",
    justifyContent: "space-between", flexWrap: "wrap", gap: 16,
    boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
  },
  heroLeft:   { display: "flex", flexDirection: "column", gap: 4 },
  heroTitle:  { fontSize: 22, fontWeight: 700, letterSpacing: "-0.3px", margin: 0, color: "#fff" },
  heroSub:    { fontSize: 13, color: "rgba(255,255,255,0.70)", margin: 0 },
  heroRight:  { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  // Balance card — prominent
  balanceCard: {
    background: `linear-gradient(135deg, ${C.teal} 0%, #007a6a 100%)`,
    borderRadius: C.radius, padding: "28px 32px",
    boxShadow: "0 4px 24px rgba(0,182,155,0.25)",
    color: "#fff", position: "relative", overflow: "hidden",
  },
  balanceLabel: { fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.8, marginBottom: 8 },
  balanceValue: { fontSize: 36, fontWeight: 900, letterSpacing: "-1px", margin: 0 },
  balanceSub:   { fontSize: 13, opacity: 0.75, marginTop: 6 },
  // Stat card
  statCard: {
    background: C.panel, borderRadius: C.radius, padding: "20px 24px",
    boxShadow: C.shadow, border: `1px solid ${C.border}`, height: "100%",
  },
  statLabel: { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight, marginBottom: 6 },
  statValue: { fontSize: 26, fontWeight: 800, color: C.text, letterSpacing: "-0.5px" },
  statSub:   { fontSize: 12, color: C.textLight, marginTop: 4 },
  // Panel
  panel: {
    background: C.panel, borderRadius: C.radius, padding: "24px 28px",
    boxShadow: C.shadow, border: `1px solid ${C.border}`, marginBottom: 24,
  },
  panelTitle: {
    fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: "-0.2px",
    marginBottom: 18, display: "flex", alignItems: "center", gap: 8,
  },
  // Form
  label:    { fontSize: 12, fontWeight: 700, color: C.textMid, marginBottom: 6, letterSpacing: "0.04em", display: "block" },
  input:    {
    background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: C.radiusSm,
    padding: "10px 14px", fontSize: 14, color: C.text, width: "100%", outline: "none",
    transition: "border 0.15s",
  },
  // Table
  table:    { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:       { padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight, borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap" },
  td:       { padding: "12px 14px", borderBottom: `1px solid ${C.border}`, color: C.textMid, verticalAlign: "middle" },
  // Buttons
  btn: (variant = "primary") => ({
    background:
      variant === "primary" ? C.blue :
      variant === "success" ? C.teal :
      variant === "danger"  ? C.accent :
      "#edf2f7",
    color: variant === "ghost" ? C.textMid : "#fff",
    border: "none", borderRadius: C.radiusSm, padding: "9px 18px",
    fontSize: 13, fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6,
    transition: "opacity 0.15s",
  }),
  // Badge
  badge: (color) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 10px", borderRadius: 20,
    fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
    background:
      color === "green"  ? C.tealSoft   :
      color === "red"    ? C.accentSoft :
      color === "yellow" ? C.orangeSoft :
      color === "purple" ? C.purpleSoft : C.blueSoft,
    color:
      color === "green"  ? C.teal   :
      color === "red"    ? C.accent :
      color === "yellow" ? C.orange :
      color === "purple" ? C.purple : C.blue,
  }),
  // Fee breakdown row
  feeRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "10px 0", borderBottom: `1px solid ${C.border}`,
    fontSize: 13, color: C.textMid,
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtUGX  = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" }) : "—";

const WD_STATUS = {
  PENDING:    { color: "purple", icon: <FiClock size={12} />,       label: "Pending" },
  PROCESSING: { color: "yellow", icon: <FiActivity size={12} />,    label: "Processing" },
  COMPLETED:  { color: "green",  icon: <FiCheckCircle size={12} />, label: "Completed" },
  FAILED:     { color: "red",    icon: <FiXCircle size={12} />,     label: "Failed" },
  CANCELLED:  { color: "red",    icon: <FiXCircle size={12} />,     label: "Cancelled" },
};

const TX_TYPE = {
  CREDIT:  { color: "green",  sign: "+", icon: <FiArrowUpRight size={12} style={{ transform: "rotate(180deg)" }} /> },
  DEBIT:   { color: "red",    sign: "−", icon: <FiArrowUpRight size={12} /> },
  HOLD:    { color: "yellow", sign: "~", icon: <FiClock size={12} /> },
  RELEASE: { color: "blue",   sign: "+", icon: <FiCheckCircle size={12} /> },
};

// ── Sub-components ─────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, iconBg }) {
  return (
    <div style={S.statCard}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <div style={S.statLabel}>{label}</div>
          <div style={S.statValue}>{value}</div>
          {sub && <div style={S.statSub}>{sub}</div>}
        </div>
        <div style={{ background: iconBg || C.blueSoft, borderRadius: 10, padding: 10 }}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function FeeBreakdown({ fees, amount }) {
  if (!fees) return null;
  return (
    <div style={{ background: C.inputBg, borderRadius: C.radiusSm, padding: "16px 18px", marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.textLight, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>
        Fee Breakdown
      </div>
      {[
        { label: "You withdraw",           value: fmtUGX(amount),              bold: false },
        { label: "MTN transfer fee",        value: fmtUGX(fees.mtnFee),         bold: false, color: C.accent },
        { label: "Lusuku absorbs (100%)",   value: `- ${fmtUGX(fees.mtnFeeAbsorbed)}`, bold: false, color: C.teal },
        { label: "You receive",             value: fmtUGX(fees.vendorReceives), bold: true },
      ].map((r, i) => (
        <div key={i} style={{ ...S.feeRow, ...(i === 3 ? { borderBottom: "none", fontWeight: 700, color: C.text, fontSize: 14 } : {}) }}>
          <span style={{ color: r.color || C.textMid }}>{r.label}</span>
          <span style={{ fontWeight: r.bold ? 800 : 500, color: r.color || (r.bold ? C.teal : C.text) }}>{r.value}</span>
        </div>
      ))}
      <div style={{ marginTop: 10, padding: "8px 12px", background: C.tealSoft, borderRadius: C.radiusSm, fontSize: 12, color: C.teal, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
        <FiShield size={12} /> Lusuku absorbs all MTN fees — you receive your full amount
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function VendorWallet() {
  const user      = getUserFromLocalStorage();
  const vendorId  = vendorOwnerIdOf(user);
  const adminRoles = ["SUPERUSER", "ADMIN"];
  const isAdmin   = adminRoles.includes(user?.result?.user_role);
  const isSeller  = user?.result?.userType === "SELLER";
  const isVendor  = isSeller || isAdmin;

  // State
  const [balance, setBalance]         = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txPagination, setTxPagination] = useState({ page: 1, pages: 1 });
  const [withdrawals, setWithdrawals] = useState([]);
  const [wdPagination, setWdPagination] = useState({ page: 1, pages: 1 });
  const [loading, setLoading]         = useState(true);
  const [txPage, setTxPage]           = useState(1);
  const [wdPage, setWdPage]           = useState(1);
  const [activeTab, setActiveTab]     = useState("transactions"); // "transactions" | "withdrawals"
  const [balanceHidden, setBalanceHidden] = useState(false);

  // Withdrawal form
  const [wdAmount, setWdAmount]       = useState("");
  const [wdPhone, setWdPhone]         = useState("");
  const [fees, setFees]               = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const feeTimer = useRef(null);

  // Auto-refresh + last-updated
  const [lastUpdated, setLastUpdated] = useState(null);
  const autoRefreshRef                = useRef(null);

  // Poll withdrawal in progress
  const [pollingId, setPollingId]     = useState(null);
  const pollRef                       = useRef(null);

  const loadAll = useCallback(async (tp = 1, wp = 1, silent = false) => {
    if (!vendorId || (isAdmin && !isSeller)) return;
    if (!silent) setLoading(true);
    try {
      const [balRes, txRes, wdRes] = await Promise.allSettled([
        apiVendorWalletBalance(vendorId),
        apiVendorWalletTransactions(vendorId, tp, 15),
        apiVendorWithdrawalHistory(vendorId, wp),
      ]);
      if (balRes.status === "fulfilled") setBalance(balRes.value.data);
      if (txRes.status === "fulfilled") {
        const d = txRes.value.data;
        setTransactions(d.transactions || []);
        setTxPagination(d.pagination || { page: 1, pages: 1 });
      }
      if (wdRes.status === "fulfilled") {
        const d = wdRes.value.data;
        setWithdrawals(d.withdrawals || d.history || []);
        setWdPagination(d.pagination || { page: 1, pages: 1 });
      }
    } catch (err) {
      if (!silent) toast.error("Failed to load wallet data");
    } finally {
      if (!silent) setLoading(false);
      setLastUpdated(new Date());
    }
  }, [vendorId]);

  useEffect(() => { loadAll(txPage, wdPage); }, [loadAll, txPage, wdPage]);

  // Auto-refresh every 30 seconds — silent background poll, no spinner
  useEffect(() => {
    autoRefreshRef.current = setInterval(() => {
      loadAll(txPage, wdPage, true);
    }, 30000);
    return () => clearInterval(autoRefreshRef.current);
  }, [loadAll, txPage, wdPage]);

  // ── Fee calculation (debounced 600ms) ──────────────────────────────────────
  useEffect(() => {
    const amt = parseFloat(wdAmount);
    if (!amt || amt <= 0) { setFees(null); return; }
    clearTimeout(feeTimer.current);
    feeTimer.current = setTimeout(async () => {
      setFeesLoading(true);
      try {
        const res = await apiVendorCalculateFees(amt);
        setFees(res.data);
      } catch { setFees(null); }
      finally { setFeesLoading(false); }
    }, 600);
    return () => clearTimeout(feeTimer.current);
  }, [wdAmount]);

  // ── Poll disbursement status after submission ───────────────────────────────
  useEffect(() => {
    if (!pollingId) return;
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await apiVendorWithdrawalStatus(pollingId);
        const s   = res.data?.status;
        if (s === "COMPLETED" || s === "SUCCESSFUL") {
          clearInterval(pollRef.current);
          setPollingId(null);
          toast.success("Withdrawal successful! Funds sent to your MTN MoMo.");
          loadAll(1, 1);
        } else if (s === "FAILED" || s === "CANCELLED") {
          clearInterval(pollRef.current);
          setPollingId(null);
          toast.error(`Withdrawal ${s.toLowerCase()}. Please try again.`);
          loadAll(1, 1);
        }
      } catch { /* ignore poll errors */ }
      if (attempts >= 24) { // stop after 2 min (24 × 5s)
        clearInterval(pollRef.current);
        setPollingId(null);
        toast.warn("Withdrawal is taking longer than expected. Check history for updates.");
        loadAll(1, 1);
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [pollingId, loadAll]);

  // ── Withdrawal submit ──────────────────────────────────────────────────────
  const handleWithdrawal = async () => {
    setConfirmModal(false);
    setSubmitting(true);
    try {
      const amt   = parseFloat(wdAmount);
      const phone = wdPhone.trim().replace(/^0/, "256").replace(/^\+/, "");
      const res   = await apiVendorWithdrawalRequest(vendorId, amt, phone);
      const wdId  = res.data?.withdrawalId;
      toast.info("Withdrawal submitted — approve the MTN PIN prompt on your phone.");
      setWdAmount("");
      setWdPhone("");
      setFees(null);
      setActiveTab("withdrawals");
      if (wdId) setPollingId(wdId);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Withdrawal failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = () => {
    const amt = parseFloat(wdAmount);
    const ph  = wdPhone.trim();
    return amt >= 10000 && ph.length >= 9 && !submitting && amt <= bal;
  };

  const validateAndConfirm = () => {
    const amt = parseFloat(wdAmount);
    if (!amt || amt < 10000)  return toast.error("Minimum withdrawal is UGX 10,000");
    if (amt > bal)           return toast.error(`Insufficient balance. Available: ${fmtUGX(bal)}`);
    const ph = wdPhone.trim();
    if (ph.length < 9)       return toast.error("Enter a valid MTN phone number");
    setConfirmModal(true);
  };

  if (!isVendor) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: C.textMid }}>
        <FiShield size={40} style={{ color: C.accent, marginBottom: 12 }} />
        <div style={{ fontSize: 16, fontWeight: 600 }}>Vendor access required</div>
        <div style={{ fontSize: 13, color: C.textLight, marginTop: 6 }}>Only vendor accounts can access this wallet view.</div>
      </div>
    );
  }

  // Admin accounts (non-seller) have no vendor wallet — redirect them to WalletDashboard
  if (isAdmin && !isSeller) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <div style={{
          display: "inline-flex", flexDirection: "column", alignItems: "center",
          background: C.panel, borderRadius: C.radius, padding: "40px 48px",
          boxShadow: C.shadow, border: `1px solid ${C.border}`, maxWidth: 440,
        }}>
          <FiShield size={40} style={{ color: C.teal, marginBottom: 16 }} />
          <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 8 }}>Admin Account</div>
          <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6, marginBottom: 24 }}>
            Admin accounts do not have a personal vendor wallet.
            Use the <strong>Disbursements</strong> dashboard to manage MTN MoMo payouts,
            check balances, and monitor disbursement status.
          </div>
          <a
            href="/admin/wallet-dashboard"
            style={{
              background: C.teal, color: "#fff", border: "none",
              borderRadius: C.radiusSm, padding: "10px 24px",
              fontSize: 13, fontWeight: 600, textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            <FiDollarSign size={14} /> Go to Disbursements
          </a>
        </div>
      </div>
    );
  }

  // Route returns { vendorId, balance: { availableBalance, pendingEscrow, totalBalance, lifetimeEarnings, lifetimeWithdrawals, walletExists } }
  const walletData      = balance?.balance ?? balance;
  const walletExists    = walletData?.walletExists !== false; // true until API says otherwise
  const bal             = walletData?.availableBalance ?? 0;
  const escrow          = walletData?.pendingEscrow ?? walletData?.pendingBalance ?? 0;
  const totalBalance    = walletData?.totalBalance ?? 0;
  const lifetimeEarned  = walletData?.lifetimeEarnings ?? 0;
  const lifetimeOut     = walletData?.lifetimeWithdrawals ?? 0;

  return (
    <div style={S.page}>

      {/* ── Hero ── */}
      <div style={S.hero}>
        <div style={S.heroLeft}>
          <div style={S.heroTitle}><FiDollarSign style={{ marginRight: 8 }} />My Wallet</div>
          <div style={S.heroSub}>Lusuku virtual credit · MTN MoMo withdrawals{lastUpdated && <span style={{ marginLeft: 10, opacity: 0.6, fontSize: 11 }}>· Updated {lastUpdated.toLocaleTimeString()} · auto-refreshes every 30s</span>}</div>
        </div>
        <div style={S.heroRight}>
          {pollingId && (
            <span style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(246,150,30,0.2)", border: "1px solid rgba(246,150,30,0.4)", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700, color: C.orange }}>
              <Spinner animation="border" size="sm" style={{ width: 10, height: 10, borderWidth: 2 }} />
              Withdrawal in progress…
            </span>
          )}
          <button style={S.btn("ghost")} onClick={() => loadAll(txPage, wdPage)} disabled={loading}>
            <FiRefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── No-wallet banner ── */}
      {!loading && balance && !walletExists && (
        <div style={{ background: C.orangeSoft, border: `1px solid #fcd99a`, borderRadius: C.radius, padding: "16px 20px", marginBottom: 24, fontSize: 13, color: C.orange, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Wallet not set up yet.</strong> Your vendor wallet will be created automatically on your next order. Contact support if this persists after placing an order.
          </div>
        </div>
      )}

      {/* ── Balance + Stats ── */}
      <Row className="g-3 mb-4">
        {/* Balance card */}
        <Col xs={12} md={12} xl={4}>
          <div style={S.balanceCard}>
            {/* decorative circle */}
            <div style={{ position: "absolute", right: -30, top: -30, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
            <div style={{ position: "absolute", right: 20, bottom: -40, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
            <div style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={S.balanceLabel}>Available Balance</div>
                <button
                  onClick={() => setBalanceHidden((h) => !h)}
                  style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center" }}
                >
                  {balanceHidden ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                </button>
              </div>
              {loading ? (
                <Spinner animation="border" size="sm" style={{ color: "#fff", opacity: 0.7 }} />
              ) : (
                <div style={S.balanceValue}>
                  {balanceHidden ? "UGX ••••••" : fmtUGX(bal)}
                </div>
              )}
              {escrow > 0 && (
                <div style={S.balanceSub}>
                  <FiClock size={11} style={{ marginRight: 4 }} />
                  {fmtUGX(escrow)} in escrow
                </div>
              )}
              <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 8 }}>
                <FiShield size={13} style={{ opacity: 0.8 }} />
                <span style={{ fontSize: 12, opacity: 0.75 }}>LUSUKU_ABSORBS fee strategy active</span>
              </div>
            </div>
          </div>
        </Col>

        {/* Stat cards */}
        <Col xs={12} md={12} xl={8}>
          <Row className="g-3 h-100">
            <Col xs={6} lg={3}>
              <StatCard
                label="Lifetime Earned"
                value={loading ? "—" : fmtUGX(lifetimeEarned)}
                sub="Total released to wallet"
                icon={<FiTrendingUp size={18} color={C.blue} />}
                iconBg={C.blueSoft}
              />
            </Col>
            <Col xs={6} lg={3}>
              <StatCard
                label="Released (Maturing)"
                value={loading ? "—" : fmtUGX(totalBalance)}
                sub="Pending 3-day maturity"
                icon={<FiDownload size={18} color={C.teal} />}
                iconBg={C.tealSoft}
              />
            </Col>
            <Col xs={6} lg={3}>
              <StatCard
                label="Total Withdrawn"
                value={loading ? "—" : fmtUGX(lifetimeOut)}
                sub="Paid to MTN MoMo"
                icon={<FiSend size={18} color={C.purple} />}
                iconBg={C.purpleSoft}
              />
            </Col>
            <Col xs={6} lg={3}>
              <StatCard
                label="Pending Withdrawals"
                value={loading ? "—" : (withdrawals.filter((w) => w.status === "PROCESSING" || w.status === "PENDING").length)}
                sub="Awaiting MTN"
                icon={<FiClock size={18} color={C.orange} />}
                iconBg={C.orangeSoft}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* ── Withdrawal Form ── */}
      <Row className="g-4">
        <Col xs={12} lg={5} xl={4}>
          <div style={S.panel}>
            <div style={S.panelTitle}>
              <FiSend size={16} color={C.blue} /> Request Withdrawal
            </div>

            {/* Amount */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <label style={{ ...S.label, marginBottom: 0 }}>Amount (UGX)</label>
                {bal > 0 && (
                  <button
                    type="button"
                    onClick={() => setWdAmount(String(bal))}
                    style={{ background: C.tealSoft, border: `1px solid ${C.teal}`, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700, color: C.teal, cursor: "pointer" }}
                  >
                    Max
                  </button>
                )}
              </div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: C.textLight, fontWeight: 600 }}>UGX</span>
                <input
                  style={{ ...S.input, paddingLeft: 48 }}
                  type="number"
                  min={10000}
                  max={bal}
                  placeholder="e.g. 50000"
                  value={wdAmount}
                  onChange={(e) => setWdAmount(e.target.value)}
                  onFocus={(e) => { e.target.style.border = `1px solid ${C.blue}`; }}
                  onBlur={(e)  => { e.target.style.border = `1px solid ${C.border}`; }}
                />
              </div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                Minimum: UGX 10,000 · Available: <strong style={{ color: bal > 0 ? C.teal : C.accent }}>{fmtUGX(bal)}</strong>
              </div>
            </div>

            {/* Phone */}
            <div style={{ marginBottom: 18 }}>
              <label style={S.label}>MTN MoMo Number</label>
              <div style={{ position: "relative" }}>
                <FiSmartphone size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.textLight }} />
                <input
                  style={{ ...S.input, paddingLeft: 34 }}
                  type="tel"
                  placeholder="07XXXXXXXX or 256XXXXXXXXX"
                  value={wdPhone}
                  onChange={(e) => setWdPhone(e.target.value)}
                  onFocus={(e) => { e.target.style.border = `1px solid ${C.blue}`; }}
                  onBlur={(e)  => { e.target.style.border = `1px solid ${C.border}`; }}
                />
              </div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>MTN Uganda numbers only (07XX or 2567XX)</div>
            </div>

            {/* Fee breakdown */}
            {feesLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textLight, marginBottom: 12 }}>
                <Spinner animation="border" size="sm" style={{ width: 12, height: 12, borderWidth: 2 }} /> Calculating fees…
              </div>
            )}
            {fees && !feesLoading && (
              <FeeBreakdown fees={fees} amount={parseFloat(wdAmount)} />
            )}

            {/* MTN-only notice */}
            <div style={{ background: C.blueSoft, border: `1px solid #c6dcf9`, borderRadius: C.radiusSm, padding: "10px 14px", marginTop: 16, marginBottom: 20, fontSize: 12, color: C.blue, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <FiInfo size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>MTN MoMo only. Airtel Money support coming in Phase 2.</span>
            </div>

            <button
              style={{ ...S.btn("success"), width: "100%", justifyContent: "center", padding: "12px 18px", fontSize: 14, opacity: canSubmit() ? 1 : 0.55 }}
              onClick={validateAndConfirm}
              disabled={!canSubmit()}
            >
              {submitting
                ? <><Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} /> Processing…</>
                : <><FiSend size={14} /> Withdraw Funds</>
              }
            </button>
          </div>
        </Col>

        {/* ── Transactions / Withdrawals ── */}
        <Col xs={12} lg={7} xl={8}>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 0, marginBottom: 0, borderBottom: `2px solid ${C.border}` }}>
            {[
              { key: "transactions", label: "Transactions", icon: <FiList size={13} /> },
              { key: "withdrawals",  label: "Withdrawals",  icon: <FiSend size={13} /> },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  background: "none", border: "none", padding: "12px 20px",
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                  borderBottom: activeTab === tab.key ? `2px solid ${C.blue}` : "2px solid transparent",
                  color: activeTab === tab.key ? C.blue : C.textLight,
                  marginBottom: -2, display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          <div style={{ ...S.panel, borderRadius: `0 ${C.radius} ${C.radius} ${C.radius}`, marginTop: 0 }}>
            {activeTab === "transactions" ? (
              <>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "36px 0", color: C.textLight }}>
                    <Spinner animation="border" size="sm" style={{ color: C.blue }} />
                    <div style={{ marginTop: 8, fontSize: 13 }}>Loading transactions…</div>
                  </div>
                ) : transactions.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "36px 0", color: C.textLight }}>
                    <FiList size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                    <div style={{ fontSize: 13 }}>No transactions yet</div>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Date</th>
                          <th style={S.th}>Type</th>
                          <th style={S.th}>Description</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => {
                          const meta = TX_TYPE[tx.type] || TX_TYPE.CREDIT;
                          return (
                            <tr key={tx._id}
                              onMouseEnter={(e) => { e.currentTarget.style.background = C.inputBg; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                            >
                              <td style={{ ...S.td, fontSize: 12, whiteSpace: "nowrap" }}>{fmtDate(tx.createdAt)}</td>
                              <td style={S.td}>
                                <span style={S.badge(meta.color)}>
                                  {meta.icon} {tx.type}
                                </span>
                              </td>
                              <td style={{ ...S.td, maxWidth: 200 }}>
                                <div style={{ fontSize: 13, color: C.text }}>{tx.description || tx.reference || "—"}</div>
                              </td>
                              <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: meta.color === "green" ? C.teal : C.accent }}>
                                {meta.sign}{fmtUGX(tx.amount)}
                              </td>
                              <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: C.textMid }}>
                                {tx.balanceAfter !== undefined ? fmtUGX(tx.balanceAfter) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {txPagination.pages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
                    <button style={S.btn("ghost")} disabled={txPage <= 1} onClick={() => setTxPage((p) => p - 1)}>
                      <FiChevronLeft size={14} />
                    </button>
                    <span style={{ fontSize: 13, color: C.textMid }}>Page {txPagination.page} of {txPagination.pages}</span>
                    <button style={S.btn("ghost")} disabled={txPage >= txPagination.pages} onClick={() => setTxPage((p) => p + 1)}>
                      <FiChevronRight size={14} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {pollingId && (
                  <div style={{ background: C.orangeSoft, border: `1px solid #fcd99a`, borderRadius: C.radiusSm, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: C.orange, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                    <Spinner animation="border" size="sm" style={{ width: 12, height: 12, borderWidth: 2 }} />
                    Waiting for PIN approval on your phone…
                  </div>
                )}
                {loading ? (
                  <div style={{ textAlign: "center", padding: "36px 0", color: C.textLight }}>
                    <Spinner animation="border" size="sm" style={{ color: C.blue }} />
                    <div style={{ marginTop: 8, fontSize: 13 }}>Loading withdrawals…</div>
                  </div>
                ) : withdrawals.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "36px 0", color: C.textLight }}>
                    <FiSend size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                    <div style={{ fontSize: 13 }}>No withdrawals yet</div>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Date</th>
                          <th style={S.th}>Status</th>
                          <th style={S.th}>Amount</th>
                          <th style={S.th}>Net Received</th>
                          <th style={S.th}>Phone</th>
                          <th style={S.th}>MTN Ref</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawals.map((w) => {
                          const meta = WD_STATUS[w.status] || WD_STATUS.PENDING;
                          return (
                            <tr key={w._id || w.withdrawalId}
                              onMouseEnter={(e) => { e.currentTarget.style.background = C.inputBg; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                            >
                              <td style={{ ...S.td, fontSize: 12, whiteSpace: "nowrap" }}>{fmtDate(w.createdAt)}</td>
                              <td style={S.td}>
                                <span style={S.badge(meta.color)}>
                                  {meta.icon} {meta.label}
                                </span>
                              </td>
                              <td style={{ ...S.td, fontWeight: 700, color: C.text }}>{fmtUGX(w.amount)}</td>
                              <td style={{ ...S.td, fontWeight: 600, color: C.teal }}>{fmtUGX(w.netAmount ?? w.amount)}</td>
                              <td style={{ ...S.td, fontSize: 12 }}>{w.phoneNumber || "—"}</td>
                              <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11, color: C.textLight }}>
                                {w.disbursementId ? w.disbursementId.slice(0, 14) + "…" : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {wdPagination.pages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
                    <button style={S.btn("ghost")} disabled={wdPage <= 1} onClick={() => setWdPage((p) => p - 1)}>
                      <FiChevronLeft size={14} />
                    </button>
                    <span style={{ fontSize: 13, color: C.textMid }}>Page {wdPagination.page} of {wdPagination.pages}</span>
                    <button style={S.btn("ghost")} disabled={wdPage >= wdPagination.pages} onClick={() => setWdPage((p) => p + 1)}>
                      <FiChevronRight size={14} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* ── Confirm Withdrawal Modal ── */}
      <Modal show={confirmModal} onHide={() => setConfirmModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.border}` }}>
          <Modal.Title>
            Confirm Withdrawal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px 28px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { label: "Amount",       value: fmtUGX(parseFloat(wdAmount) || 0) },
              { label: "To",           value: wdPhone },
              { label: "You receive",  value: fees ? fmtUGX(fees.vendorReceives) : fmtUGX(parseFloat(wdAmount) || 0), highlight: true },
              { label: "MTN fee",      value: fees ? fmtUGX(fees.mtnFeeAbsorbed) + " (paid by Lusuku)" : "Calculated on submit" },
            ].map((r) => (
              <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13, color: C.textMid }}>{r.label}</span>
                <span style={{ fontSize: 13, fontWeight: r.highlight ? 800 : 600, color: r.highlight ? C.teal : C.text }}>{r.value}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, background: C.orangeSoft, border: `1px solid #fcd99a`, borderRadius: C.radiusSm, padding: "10px 14px", fontSize: 12, color: C.orange, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <FiAlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            An MTN PIN prompt will appear on <strong>{wdPhone}</strong>. Approve it within 3 minutes.
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setConfirmModal(false)}>Cancel</Button>
          <Button size="sm" onClick={handleWithdrawal}
            style={{ background: C.teal, border: "none", fontWeight: 700 }}>
            <FiSend size={13} style={{ marginRight: 6 }} /> Confirm & Send
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
